import { ComponentPropsWithoutRef, ForwardedRef, MouseEventHandler, forwardRef } from 'react';
import * as Avatar from '@givebutter/primitives/avatar';
import { Button } from '@givebutter/primitives/button';
import { faClose, faUser } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getAvatarFallbackColor } from 'components/util/getAvatarFallbackColor';
import { getInitialsFromName } from 'helpers/formatting';
import { twMerge } from 'tailwind-merge';
import { CrmMember } from 'types/domain';

interface TaskAssigneeContainerProps extends ComponentPropsWithoutRef<'div'> {}
const TaskAssigneeContainer = forwardRef(
    (
        { className, children, ...rest }: TaskAssigneeContainerProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        return (
            <div
                className={twMerge('rounded-lg border border-gray-200 px-3 py-2', className)}
                ref={forwardedRef}
                {...rest}
            >
                {children}
            </div>
        );
    },
);

interface TaskAssigneeProps extends ComponentPropsWithoutRef<'div'> {
    assignee: CrmMember | null;
    onRemove?: MouseEventHandler<HTMLButtonElement>;
}
const TaskAssignee = forwardRef(
    (
        { assignee, onRemove, className, children, ...rest }: TaskAssigneeProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        return (
            <div
                className={twMerge('flex items-center gap-1.5', className)}
                ref={forwardedRef}
                {...rest}
            >
                {assignee ? (
                    <>
                        <Avatar.Root size="2xs">
                            <Avatar.Fallback
                                className={getAvatarFallbackColor(assignee.first_name)}
                                aria-label={`Avatar for ${assignee.first_name} ${assignee.last_name}`}
                            >
                                {getInitialsFromName(assignee.first_name, assignee.last_name)}
                            </Avatar.Fallback>
                        </Avatar.Root>
                        <span className="text-sm">
                            {assignee.first_name} {assignee.last_name}
                        </span>
                        {onRemove ? (
                            <Button
                                variant="none"
                                color="neutral"
                                onClick={onRemove}
                                className="px-1"
                            >
                                <FontAwesomeIcon icon={faClose} />
                            </Button>
                        ) : null}
                    </>
                ) : (
                    <>
                        <span className="flex h-5 w-5 border-spacing-2 items-center justify-center rounded-full border border-dashed border-gray-400">
                            <FontAwesomeIcon icon={faUser} size="xs" className="text-gray-400" />
                        </span>
                        <span className="text-sm">No assignee</span>
                    </>
                )}
            </div>
        );
    },
);

const Root = TaskAssigneeContainer;
const Content = TaskAssignee;

export {
    Root,
    Content,
    //
    TaskAssigneeContainer,
    TaskAssignee,
};
